import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { URL_CALLCENTER_PORTAL, KEY_RECOIL } from '@/shared/config';
import { dateToStringWithDailyTimeZone, zeroPad } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import {
  selectChangeStripData,
  selectIsLoadingStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { checkStatusStripChange } from '../handler';
import type { EditStripBoxProps } from './index';

/**
 * Drives the strip editing panel for the strips-management tab, exposing the selected strip's event metadata alongside title and comment editing. Reads the selected strip, pending strip changes and loading or saving flags from the report store, merges any pending change over the strip, mirrors it into local form state on selection, keeps per-strip unsaved edits in a ref, formats the captured time in the facility time zone, and returns apply, cancel, change, key-down and portal-navigation handlers.
 *
 * @param props - Panel props; isActive is read to reset validation errors when the tab is re-entered.
 * @returns The strip edit view model with selectedData, isDisabled, loading and saving flags, form state, event id, type, trigger time, symptoms, captured time and the edit handlers.
 */
const useEditStripBox = ({ isActive }: Pick<EditStripBoxProps, 'isActive'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip changes for the strips-management tab into the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New pending strip changes, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);
  const unSaveInfo = useRef<any[]>([]);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const isSavingStripManagement = useReportStore((s) => s.isSavingStripmanagement);

  const [state, setState] = useMergeState<any>({
    technicianComment: '',
    title: '',
    titleError: '',
    commentError: '',
    isRemove: false,
    type: '',
    isDeviceEvent: false,
  });

  const { eventId, eventType, triggerTime, symptoms } = state || {};

  const selectedData: any = useMemo(() => {
    const findChange = _.find(changeStripData, (item) => item.id === selectedStrip?.id);
    if (findChange) return { ...findChange, isChanged: true };
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark; originalInfo
    // is only on the local strip shapes built in StripsManagementV2/handler.ts - pre-existing gap.
    return selectedStrip
      ? { ...selectedStrip, isRemoved: false, ...(selectedStrip as any).originalInfo }
      : {};
  }, [selectedStrip, changeStripData]);

  const isDisabled = selectedData?.isRemoved || _.isEmpty(selectedData);

  /**
   * Opens the selected strip's event in the call-centre portal. Builds the portal events URL from the configured base and the selected strip id and opens it in a new browser tab.
   *
   * @returns Nothing.
   */
  const onClickNavigate = () => {
    const url = `${URL_CALLCENTER_PORTAL}events/${selectedStrip.id}`;
    window.open(url, '_blank');
  };

  /**
   * Records an edit to the selected strip's title or technician comment. Merges the field into local state, clears the matching validation error for title or technicianComment, then either updates the strip's pending entry in the unsaved-info ref or pushes a new entry keyed by the selected strip id.
   *
   * @param name - Field being edited, either title or technicianComment.
   * @param value - New value typed into that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const updateData = { [name]: value };
    if (name === 'title') {
      _.assign(updateData, { titleError: '' });
    }
    if (name === 'technicianComment') {
      _.assign(updateData, { commentError: '' });
    }
    setState(updateData);
    const savedData = _.find(unSaveInfo.current, (item) => item.id === selectedData.id);
    if (savedData) {
      _.assign(savedData, {
        [name]: value,
      });
    } else {
      unSaveInfo.current.push({
        id: selectedStrip.id,
        [name]: value,
      });
    }
  };

  /**
   * Keeps keystrokes inside the strip title and comment inputs from reaching the report's global key handlers. Stops propagation of the keyboard event and does nothing else.
   *
   * @param e - Keyboard event raised by the input.
   * @returns Nothing.
   */
  const onKeyDownInput = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Drops the pending unsaved value for one field of the selected strip. Deep-clones the unsaved-info ref, finds the entry matching the selected strip id, sets its title or technicianComment back to undefined, and stores the clone back on the ref.
   *
   * @param name - Field to clear, either title or anything else meaning the technician comment.
   * @returns Nothing.
   */
  const resetSaveData = (name: any) => {
    const cloneSavedData = _.cloneDeep(unSaveInfo.current);
    const savedData = _.find(cloneSavedData, (item) => item.id === selectedData.id);
    if (savedData) {
      if (name === 'title') {
        savedData.title = undefined;
      } else {
        savedData.technicianComment = undefined;
      }
    }
    unSaveInfo.current = cloneSavedData;
  };

  /**
   * Commits the edited title or technician comment onto the selected strip. Rejects a blank value by setting the title or comment error message, otherwise copies the value onto a clone of the selected strip, folds it into the pending strip changes via checkStatusStripChange, writes them to the report store and clears that field's unsaved entry.
   *
   * @param name - Field being applied, either title or anything else meaning the technician comment.
   * @returns Nothing.
   */
  const onClickApplyChange = (name: any) => {
    const cloneSelectedStrip = { ...selectedData };
    if (name === 'title') {
      if (!state.title?.trim()) {
        setState({ titleError: 'Please enter a title' });
        return;
      }
      cloneSelectedStrip.title = state.title;
    } else {
      if (!state.technicianComment?.trim()) {
        setState({ commentError: 'Please enter a comment' });
        return;
      }
      cloneSelectedStrip.comment = state.technicianComment;
    }
    const changesData = checkStatusStripChange([cloneSelectedStrip], changeStripData);
    setChangeStripData(changesData);
    resetSaveData(name);
  };

  /**
   * Reverts an in-progress title or comment edit on the selected strip. Clears the field's pending unsaved value, then restores the input from the selected strip's stored title or comment and blanks the corresponding validation error.
   *
   * @param name - Field to revert, either title or anything else meaning the technician comment.
   * @returns Nothing.
   */
  const onClickCancelChange = (name: any) => {
    resetSaveData(name);
    if (name === 'title') {
      setState({ title: selectedData.title, titleError: '' });
      return;
    }
    setState({ technicianComment: selectedData.comment, commentError: '' });
  };

  useEffect(() => {
    if (!_.isEmpty(selectedData)) {
      const {
        comment,
        title,
        type,
        friendlyId,
        isDeviceEvent,
        triggerTime,
        typeText,
        symptoms,
        isOtherEvent,
        id,
        isRemoved,
        isChanged,
        originalInfo,
      } = selectedData || {};
      const updateData = {
        title: isChanged ? title : originalInfo?.title,
        technicianComment: isChanged ? comment : originalInfo?.comment,
        id,
      };
      const savedData = _.find(unSaveInfo.current, (item) => item.id === id);
      if (savedData) {
        updateData.title = !_.isNil(savedData.title) ? savedData.title : updateData.title;
        updateData.technicianComment = !_.isNil(savedData.technicianComment)
          ? savedData.technicianComment
          : updateData.technicianComment;
      }

      setState({
        ...selectedData,
        isRemoved,
        ...updateData,
        type,
        eventId: zeroPad(friendlyId),
        eventType: typeText,
        triggerTime,
        symptoms,
        isDeviceEvent,
        isOtherEvent,
      });
    }
  }, [selectedData]);

  useEffect(() => {
    setState({
      titleError: '',
      commentError: '',
    });
  }, [selectedData.id, isActive]);

  const capturedTime = useMemo(
    () =>
      state.stripStart
        ? dateToStringWithDailyTimeZone({
            startStudyDate: state.startStudyDate,
            isoDate: state.stripStart,
            timezone: state.timezone,
            facility: state.facility,
          })
        : '',
    [state],
  );

  return {
    selectedData,
    isDisabled,
    isLoadingStrip,
    isSavingStripManagement,
    state,
    eventId,
    eventType,
    triggerTime,
    symptoms,
    capturedTime,
    onClickNavigate,
    onChange,
    onKeyDownInput,
    onClickApplyChange,
    onClickCancelChange,
  };
};

export default useEditStripBox;
